import React, { FC, FormEvent, useState, useEffect, Fragment } from "react";
import Icon from "../common/icon";
import Ripple from "../common/ripple";
import Dropdown from "../common/dropdown";
import FixtureState from "../fixtureState";
import * as roles from "../../store/role/types";
import * as permissions from "../../store/role/types";
import ConfirmationModal from "../common/confirmationModal";
import ProtectedComponent from "../common/protectedComponent";
import * as constants from "../../constants/fixture.constants";
import { FixtureStatus as FixtureStatusType } from "../../utils/hooks/useFixtures";
import { useFixtureMetadata } from "../../store/fixtureMetadata/useFixtureMetadata";
import { ReactComponent as UploadedToHudl } from "../../assets/images/fixture/uploadedToHudl.svg";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useLogin } from "../../store/auth/useLogin";
import { can } from "../../store/role";
import http from "../../services/http.services";
import * as resources from "../../constants/resources.constants";

import styles from "./fixtureFile.module.scss";

export interface Metadata {
    progress?: number;
}

export interface FixtureFileProps {
    id: string;
    label: string;
    state: FixtureStatusType;
    selected: boolean;
    metadata?: Metadata;
    uploadedToHudl?: boolean;
    storageClass?: string;
    restoreStatus?: string;
    restoreExpiry?: string;
    onClick: (id: string) => void;
    onClickOption: (target: string, id: string) => void;
}

const FixtureFile: FC<FixtureFileProps> = ({
    id,
    label,
    state,
    selected,
    metadata,
    uploadedToHudl,
    storageClass,
    restoreStatus,
    restoreExpiry,
    onClick,
    onClickOption
}) => {
    const { competitionId } = useFixtureMetadata();

    const [isOpen, setIsOpen] = useState(false);
    const [active, setActive] = useState(false);
    const [target, setTarget] = useState("");

    const { auth } = useLogin();
    const isGlacierUser = auth.user?.role ? can(auth.user.role, roles.glacierRole) : false;

    const [localStorageClass, setLocalStorageClass] = useState(storageClass);
    const [localRestoreStatus, setLocalRestoreStatus] = useState(restoreStatus);
    const [localRestoreExpiry, setLocalRestoreExpiry] = useState(restoreExpiry);

    useEffect(() => {
        setLocalStorageClass(storageClass);
        setLocalRestoreStatus(restoreStatus);
        setLocalRestoreExpiry(restoreExpiry);
    }, [storageClass, restoreStatus, restoreExpiry]);

    const fixtureFileClasses = `${styles.fixtureFile} ${selected ? styles.selected : ""} ${
        active ? styles.active : ""
    }`;

    useEffect(() => {
        if (target) {
            handleShowModal();
        } else {
            handleHideModal();
        }
    }, [target]);

    const handleOpen = () => setActive(true);

    const handleClose = () => setActive(false);

    const handleClick = (event: FormEvent<EventTarget>) => {
        const { id: value } = event.currentTarget as Element;

        if (value === constants.DELETE_FILE) {
            setTarget(value);
        } else if (value === constants.RESTORE_FROM_GLACIER) {
            http
                .post(
                    resources.FILE_RESTORE.replace("{id}", id),
                    {},
                    { withCredentials: true }
                )
                .then(({ data }: { data: { storage_class: string; restore_status: string; restore_expiry?: string } }) => {
                    setLocalStorageClass(data.storage_class);
                    setLocalRestoreStatus(data.restore_status);
                    setLocalRestoreExpiry(data.restore_expiry);
                })
                .catch(() => {});
        } else {
            onClickOption(value, id);
        }
    };

    const handleShowModal = () => setIsOpen(true);

    const handleHideModal = () => setIsOpen(false);

    const handleConfirmModal = () => {
        onClickOption(target, id);
        setTarget("");
    };

    const getGlacierIconConfig = (): {
        iconProp: IconProp;
        tooltip: string;
        className: string;
    } | null => {
        if (localRestoreStatus === "AVAILABLE") {
            const expiry = localRestoreExpiry
                ? new Date(localRestoreExpiry).toLocaleDateString()
                : "";
            return {
                iconProp: ["fas", "tint"],
                tooltip: `${constants.RESTORE_AVAILABLE} ${expiry}`.trim(),
                className: styles.glacierAvailable,
            };
        }
        if (localRestoreStatus === "IN_PROGRESS") {
            return {
                iconProp: ["fas", "hourglass-half"],
                tooltip: constants.RESTORE_IN_PROGRESS,
                className: styles.glacierRestoring,
            };
        }
        if (localStorageClass === "DEEP_ARCHIVE" || localStorageClass === "GLACIER") {
            return {
                iconProp: ["fas", "snowflake"],
                tooltip: constants.DEEP_GLACIER,
                className: styles.glacierFrozen,
            };
        }
        return null;
    };

    const dropdownSuggestions = (): string[] => {
        const base = [constants.DOWNLOAD, constants.DELETE_FILE];
        const isArchived =
            localStorageClass === "DEEP_ARCHIVE" || localStorageClass === "GLACIER";
        if (isGlacierUser && isArchived && !localRestoreStatus) {
            return [constants.RESTORE_FROM_GLACIER, ...base];
        }
        return base;
    };

    return (
        <Fragment>
            <div id={id.toString()} className={fixtureFileClasses} onClick={() => onClick(id)}>
                <div className={styles.fixtureHeading}>
                    <div className={styles.label}>{label}</div>
                    <div className={styles.fileIcons}>
                        {uploadedToHudl && (
                            <Icon
                                icon={{ icon: <UploadedToHudl />, type: "Svg" }}
                                tooltip={constants.UPLOADED_TO_HUDL}
                                classes={styles.uploadedToHudl}
                            />
                        )}
                        {(() => {
                            const glacierConfig = getGlacierIconConfig();
                            return glacierConfig ? (
                                <ProtectedComponent roles={roles.glacierRole}>
                                    <Icon
                                        icon={{ icon: glacierConfig.iconProp, type: "FontAwesomeIcon", size: "1x" }}
                                        tooltip={glacierConfig.tooltip}
                                        classes={glacierConfig.className}
                                    />
                                </ProtectedComponent>
                            ) : null;
                        })()}
                    </div>
                </div>
                <div className={styles.fixtureState}>
                    <FixtureState
                        state={state}
                        classes={styles.fixtureState}
                        endAdornment={metadata?.progress ? `(${metadata?.progress}%)` : undefined}
                    />
                </div>
                <ProtectedComponent
                    roles={roles.manageFileRole}
                    permissions={{ competitionId: competitionId || "", permissions: permissions.manageFile }}
                >
                    <div className={styles.dropdown}>
                        <Dropdown
                            id={constants.MANAGE_FILE}
                            icon={{ icon: { icon: ["fas", "ellipsis-v"], type: "FontAwesomeIcon", size: "1x" } }}
                            classes={`btn btn-circle-sm icon btn-white ${styles.button}`}
                            rippleColor={"black"}
                            onClick={handleClick}
                            onOpen={handleOpen}
                            onClose={handleClose}
                            tooltip={constants.MANAGE_FILE}
                            suggestions={dropdownSuggestions()}
                        />
                    </div>
                </ProtectedComponent>
                <div className={styles.rippleContainer}>
                    <Ripple rippleColor="black"></Ripple>
                </div>
            </div>
            <ConfirmationModal
                title={constants.DELETE_FILE_CONFIRMATION.replace("{file}", label)}
                icon={{ icon: ["far", "trash-alt"], type: "FontAwesomeIcon", size: "2x" }}
                isOpen={isOpen}
                onCancel={handleHideModal}
                onConfirm={handleConfirmModal}
                onRequestClose={handleHideModal}
            ></ConfirmationModal>
        </Fragment>
    );
};

export default FixtureFile;
